/**
 * The shared COMPOSITION of an email's document list and its PDF preview — the piece the expanded
 * timeline row and the full message view both render
 * (`docs/specs/strategic-architecture/governed-email-content-retrieval-spec.md` §3.1).
 *
 * The split is deliberate and matches `InlineMessageBody`:
 *
 *   * the **status** read stays with each surface — every surface calls `useCrmEmailContent` itself,
 *     through its own governed, audited door, so two surfaces can never share one authorization;
 *   * the **behaviour** of a click lives here, once. View and Download differ only in a disposition
 *     and what happens to the resulting object URL, and two copies of "fetch bytes, mint a URL,
 *     remember to revoke it" is how one copy ends up not revoking.
 *
 * ⛔ Bytes are fetched ONLY on an explicit View/Download click — never on render, never on open of
 * the row, never for siblings (spec §3.1: "Attachment bytes load only on View/Download", and no
 * sibling-file prefetch). The status envelope alone is what an open costs.
 *
 * ⛔ Every object URL this component mints is revoked: the preview's by `PdfViewer`'s effect
 * cleanup (close, unmount, or a different file), the download's immediately after the click has
 * been dispatched. A `blob:` URL left alive is a readable handle on the bytes that outlives the
 * authorization that produced them.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { fetchCrmAttachmentBlob } from '../../hooks/useCrm';
import type { CrmEmailAttachmentItem, CrmEmailContent } from '../../types';
import type { OnDemandState } from '../../hooks/useCrmContentOnDemand';
import { AttachmentList } from './AttachmentList';
import styles from './AttachmentList.module.css';
import { FETCH_FAILED_COPY, type AttachmentListState } from './attachmentDisplay';
import { PdfViewer } from './PdfViewer';

export interface EmailAttachmentsProps {
  emailId: number;
  /** This surface's own content query, already reduced by `attachmentListStateFromQuery`. */
  state: AttachmentListState;
  /** The envelope, which the caller passes ONLY when `state === 'ready'`. */
  content?: CrmEmailContent;
  /**
   * EC-5 — THIS SURFACE'S OWN content door, e.g. `/api/crm/email/9/content` or the record-context
   * `/api/crm/lists/…/evidence/…/email-content`.
   *
   * ⛔ It is a prop and not a constant because the record surface must never fall back to the
   * generic email route to route around its own denial (spec §9). Every byte read and every
   * acquisition on a record surface goes through the record door, and the way to guarantee that is
   * to make the door the thing the caller hands down.
   */
  contentBase?: string;
  /** The on-demand loop for this open, from `useCrmContentOnDemand`. Omitted ⇒ stored-only. */
  onDemand?: OnDemandState;
  className?: string;
}

export function EmailAttachments({
  emailId, state, content, contentBase, onDemand, className,
}: EmailAttachmentsProps) {
  const [preview, setPreview] = useState<{ blob: Blob; name: string | null } | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [failed, setFailed] = useState(false);
  // Guards a late response from a click the user has since navigated away from: a Blob that arrives
  // after unmount must not be turned into an object URL nobody can close.
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  // ⛔ A different message in the SAME mounted component (the modal moves `msg` from one email to
  // the next) must not keep the previous message's preview open. Closing it also revokes its object
  // URL, through PdfViewer's effect cleanup.
  //
  // Adjusted DURING RENDER, not in an effect: React's documented "reset state when a prop changes"
  // pattern re-renders immediately with the new state and never paints the stale preview for a
  // frame, which an effect would. (Both current callers also mount this under a `key={emailId}`;
  // this is what keeps the component correct for a caller that does not.)
  const [lastEmailId, setLastEmailId] = useState(emailId);
  if (emailId !== lastEmailId) {
    setLastEmailId(emailId);
    setPreview(null);
    setFailed(false);
  }

  // Which files this open has an acquisition in flight for. Derived from the resource keys the
  // loop hands back, so the list and the loop cannot disagree about what is busy.
  const pendingAttachmentIds = new Set<number>();
  onDemand?.pending.forEach((key) => {
    if (key.startsWith('attachment:')) pendingAttachmentIds.add(Number(key.slice(11)));
  });

  // The record door's byte path hangs off the same base as its status door; the generic email
  // surface passes none and keeps the default route.
  const attachmentBase = contentBase && contentBase.endsWith('/email-content')
    ? contentBase.slice(0, -'/email-content'.length)
    : undefined;

  const open = useCallback(async (item: CrmEmailAttachmentItem, mode: 'inline' | 'attachment') => {
    if (busyId != null) return;           // one gated byte read at a time, per surface
    setBusyId(item.id);
    setFailed(false);
    try {
      // ⛔ Through THIS surface's door — the record timeline reads its own record-context route
      // and never the generic email one, denied or not (spec §9).
      const blob = await fetchCrmAttachmentBlob(emailId, item.id, mode, attachmentBase);
      if (!alive.current) return;
      if (mode === 'inline') {
        setPreview({ blob, name: item.name });
        return;
      }
      // Download: mint, click, revoke. The URL exists for exactly as long as the dispatch needs it.
      const url = URL.createObjectURL(blob);
      try {
        const a = document.createElement('a');
        a.href = url;
        a.download = item.name || 'attachment';
        document.body.appendChild(a);
        a.click();
        a.remove();
      } finally {
        URL.revokeObjectURL(url);
      }
    } catch {
      // The refusal reasons (403 revoked, 404 quarantined/missing, 503 scan pending) are already
      // told by the status envelope on the next read; a byte click that fails says only that it
      // failed, and never describes a threat (spec §3.2).
      if (alive.current) setFailed(true);
    } finally {
      if (alive.current) setBusyId(null);
    }
  }, [attachmentBase, busyId, emailId]);

  return (
    <div className={className}>
      <AttachmentList
        state={state}
        // EC-5 — the acquisition affordances. `onRetrieve` is present only when this surface was
        // given an on-demand loop; without one the list is stored-only, exactly as in Release A.
        onRetrieve={onDemand ? (item) => onDemand.fetchNow({ attachment: item.id }) : undefined}
        pendingIds={pendingAttachmentIds}
        manifestBusy={onDemand?.pending.has('manifest') === true}
        budgetSpent={onDemand?.budgetSpent === true}
        onRefresh={onDemand ? onDemand.refresh : undefined}
        // ⛔ The envelope is handed over ONLY in `ready`. The list refuses to read it in any other
        // state as well, so the stale-filename leak is closed at both ends (review BLOCKER 1).
        content={state === 'ready' ? content : undefined}
        // ⛔ A failed byte CLICK does NOT collapse the list — it reports itself below. Losing the
        // whole manifest because one download failed is a worse answer than the failure itself,
        // which is why the click's failure is separate state and not folded into `state`.
        busyId={busyId}
        onView={(item) => { void open(item, 'inline'); }}
        onDownload={(item) => { void open(item, 'attachment'); }}
      />
      {failed && (
        <div data-attachment-fetch="error" role="alert" className={styles.fetchError}>
          {FETCH_FAILED_COPY}
        </div>
      )}
      <PdfViewer
        blob={preview?.blob ?? null}
        name={preview?.name}
        onClose={() => setPreview(null)}
      />
    </div>
  );
}

export default EmailAttachments;
